import React, { useState, useEffect } from "react"
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col,
} from "reactstrap"
import { RiUserAddLine } from "react-icons/ri"
import { toast } from "react-toastify"
import Select from "react-select"
import API from "../../api/axios"
import { useFormik } from "formik"
import * as Yup from "yup"
// import { FaEye, FaEyeSlash } from "react-icons/fa"


function UserForm({ refresh }) {
  const defaultPassword = "Q9avXoGKKNncWpy"
  const [open, setOpen] = useState(false)
  const [useDefaultPassword, setUseDefaultPassword] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [allSpecialities, setAllSpecialities] = useState([])
  const [selectedServices, setSelectedServices] = useState([])

  const handleClickOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const togglePasswordVisibility = () => setShowPassword(prev => !prev)

  const handleDefaultPasswordToggle = () => {
    setUseDefaultPassword(prev => !prev)
    formik.setFieldValue(
      "password",
      !useDefaultPassword ? "" : defaultPassword,
    )
  }

  // Formik setup
  const formik = useFormik({
    initialValues: {
      fname: "",
      lname: "",
      phone: "",
      email: "",
      gender: "",
      role: "Admin",
      serviceId: [],
      password: defaultPassword,
      isActive: true,
    },
    validationSchema: Yup.object({
      fname: Yup.string().required("First name is required"),
      lname: Yup.string().required("Last name is required"),
      phone: Yup.string()
        .matches(/^[0-9]{10}$/, "Phone must be 10 digits")
        .required("Phone number is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      gender: Yup.string().required("Gender is required"),
      role: Yup.string().required("Role is required"),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required"),
    }),
    onSubmit: async values => {
      try {
        const requestData = {
          ...values,
          serviceId: selectedServices.map(s => s.value),
        }

        const authToken = localStorage.getItem("authToken")
        const response = await API.post(
          "/users",
          requestData,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              "Content-type": "application/json",
            },
          },
        )

        toast.success("User added successfully!", { autoClose: 2000 })

        formik.resetForm()
        setSelectedServices([])
        refresh()
        handleClose()
      } catch (error) {
        if (error?.response?.status === 409) {
          toast.error(
            "User already exists with the same fname, lname, and phone.",
          )
        } else {
          console.error("create user failed", error)
          toast.error("Error: Could not add user.")
        }
      }
    },
  })

  useEffect(() => {
    const fetchSpecialities = async () => {
      try {
        const authToken = localStorage.getItem("authToken")
        const response = await API.get("/services", {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        })
        const uniqueSpecialities = Array.from(
          new Set(response.data.map(s => JSON.stringify(s))),
        ).map(s => JSON.parse(s))
        setAllSpecialities(uniqueSpecialities)
      } catch (err) {
        console.error("Error fetching services", err)
      }
    }

    fetchSpecialities()
  }, [])

  return (
    <div>
      <Button
        color="primary"
        onClick={handleClickOpen}
        className="float-right px-1 border py-2 mb-2 mt-2"
      >
        <RiUserAddLine size={25} /> Add User
      </Button>

      <Modal isOpen={open} toggle={handleClose}>
        <ModalHeader toggle={handleClose}>Add New User</ModalHeader>
        <ModalBody>
          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="fname">First Name</Label>
                  <Input
                    type="text"
                    id="fname"
                    name="fname"
                    value={formik.values.fname}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.fname && formik.errors.fname && (
                    <div className="text-danger">{formik.errors.fname}</div>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="lname">Last Name</Label>
                  <Input
                    type="text"
                    id="lname"
                    name="lname"
                    value={formik.values.lname}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.lname && formik.errors.lname && (
                    <div className="text-danger">{formik.errors.lname}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="phone">Phone</Label>
                  <Input
                    type="text"
                    id="phone"
                    name="phone"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.phone && formik.errors.phone && (
                    <div className="text-danger">{formik.errors.phone}</div>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="gender">Gender</Label>
                  <Input
                    type="select"
                    name="gender"
                    value={formik.values.gender}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </Input>
                  {formik.touched.gender && formik.errors.gender && (
                    <div className="text-danger">{formik.errors.gender}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label for="email">Email</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.email && formik.errors.email && (
                <div className="text-danger">{formik.errors.email}</div>
              )}
            </FormGroup>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="role">Role</Label>
                  <Input
                    type="select"
                    name="role"
                    value={formik.values.role}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <option value="">Select Role</option>
                    <option value="Admin">Admin</option>
                    <option value="Staff">Staff</option>
                    <option value="Doctor">Doctor</option>
                  </Input>
                  {formik.touched.role && formik.errors.role && (
                    <div className="text-danger">{formik.errors.role}</div>
                  )}
                </FormGroup>
              </Col>

              {formik.values.role === "Doctor" && (
                <Col md={6}>
                  <FormGroup>
                    <Label for="serviceId">Speciality</Label>
                    <Select
                      isMulti
                      name="serviceId"
                      options={allSpecialities.map(service => ({
                        label: service.name,
                        value: service.id,
                      }))}
                      value={selectedServices}
                      onChange={selected => {
                        setSelectedServices(selected)
                        formik.setFieldValue(
                          "serviceId",
                          selected.map(s => s.value),
                        )
                      }}
                    />
                  </FormGroup>
                </Col>
              )}
            </Row>

            <FormGroup tag="fieldset">
              <Label>Status</Label>
              <FormGroup check inline>
                <Label check>
                  <Input
                    type="radio"
                    name="isActive"
                    value="true"
                    checked={formik.values.isActive === true}
                    onChange={() => formik.setFieldValue("isActive", true)}
                  />{" "}
                  Active
                </Label>
              </FormGroup>
              <FormGroup check inline>
                <Label check>
                  <Input
                    type="radio"
                    name="isActive"
                    value="false"
                    checked={formik.values.isActive === false}
                    onChange={() => formik.setFieldValue("isActive", false)}
                  />{" "}
                  Inactive
                </Label>
              </FormGroup>
            </FormGroup>
  <FormGroup check>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={useDefaultPassword}
                    onClick={handleDefaultPasswordToggle}
                  />
                  Use default password
                </Label>
              </FormGroup>
            <FormGroup>
              <Label for="password">Password</Label>
              <Input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={useDefaultPassword}
              />
              {formik.touched.password && formik.errors.password && (
                <div className="text-danger">{formik.errors.password}</div>
              )}
              <Button
                type="button"
                color="link"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? "Hide" : "Show"} Password
              </Button>
              {/* <FormGroup check>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={useDefaultPassword}
                    onClick={handleDefaultPasswordToggle}
                  />
                  Use default password
                </Label>
              </FormGroup> */}
            </FormGroup>

            <Button type="submit" color="primary">
              Submit
            </Button>
          </Form>
        </ModalBody>
      </Modal>
    </div>
  )
}

export default UserForm
